import type {
  ApiDocument,
  ApiFolder,
  DeleteDocumentResponse,
  ShareDocumentRequest,
  ShareDocumentResponse,
  UpdateDocumentRequest,
  UpdateDocumentResponse,
} from "@workith/shared";
import { canManageForRole, canWriteForRole, getSharedDriveCapabilities } from "@workith/shared";
import { DropdownMenuItem, DropdownMenuSeparator } from "@workith/ui";
import { Edit, Folder, Link2, Lock, Share2, Trash2, Unlock } from "lucide-react";
import { Fragment, useState } from "react";
import api from "../../lib/api";
import { MoveDocumentDialog } from "./MoveDocumentDialog";
import { RowActionsMenu } from "./RowActionsMenu";

interface DocumentActionsProps {
  document: ApiDocument;
  folders: ApiFolder[];
  onUpdate: () => void;
}

function documentErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function canRenameDocument(document: ApiDocument): boolean {
  if (document.folder_scope === "shared_drive") {
    return getSharedDriveCapabilities(document.user_role).canWrite;
  }

  return canWriteForRole(document.user_role);
}

export function canDeleteDocument(document: ApiDocument): boolean {
  if (document.folder_scope === "shared_drive") {
    return getSharedDriveCapabilities(document.user_role).canManage;
  }

  return canManageForRole(document.user_role);
}

export function canMoveDocument(document: ApiDocument): boolean {
  if (document.folder_scope === "shared_drive") {
    return getSharedDriveCapabilities(document.user_role).canWrite;
  }

  return canManageForRole(document.user_role);
}

export function canToggleDocumentVisibility(document: ApiDocument): boolean {
  return document.folder_scope !== "shared_drive" && canManageForRole(document.user_role);
}

export function canShareDocument(document: ApiDocument): boolean {
  return (
    document.folder_scope === "shared_drive" ||
    document.visibility === "public" ||
    canManageForRole(document.user_role)
  );
}

export function DocumentActions({ document, folders, onUpdate }: DocumentActionsProps) {
  const [moveOpen, setMoveOpen] = useState(false);
  const isSharedDriveDocument = document.folder_scope === "shared_drive";
  const canRename = canRenameDocument(document);
  const canMove = canMoveDocument(document);
  const canDelete = canDeleteDocument(document);
  const canToggleVisibility = canToggleDocumentVisibility(document);
  const canShare = canShareDocument(document);

  const handleRename = async () => {
    const newName = prompt("New name:", document.name);
    if (!newName || newName === document.name) return;

    const request: UpdateDocumentRequest = { name: newName };

    try {
      await api.put<UpdateDocumentRequest, UpdateDocumentResponse>(
        `/api/documents/${document.id}`,
        request
      );
      onUpdate();
    } catch (err) {
      console.error("Failed to rename:", err);
      alert(documentErrorMessage(err, "Failed to rename document"));
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${document.name}"?`)) return;

    try {
      await api.delete<DeleteDocumentResponse>(`/api/documents/${document.id}`);
      onUpdate();
    } catch (err) {
      console.error("Failed to delete:", err);
      alert(documentErrorMessage(err, "Failed to delete document"));
    }
  };

  const handleToggleVisibility = async () => {
    const newVisibility = document.visibility === "private" ? "public" : "private";

    const request: UpdateDocumentRequest = { visibility: newVisibility };

    try {
      await api.put<UpdateDocumentRequest, UpdateDocumentResponse>(
        `/api/documents/${document.id}`,
        request
      );
      onUpdate();
    } catch (err) {
      console.error("Failed to update visibility:", err);
      alert(documentErrorMessage(err, "Failed to update visibility"));
    }
  };

  const handleShare = () => {
    const copyLink = (url: string, successMessage: string) => {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          alert(successMessage);
        })
        .catch((error) => {
          console.error("Failed to copy link:", error);
          alert("Failed to copy link");
        });
    };

    if (!canShare) {
      return;
    }

    if (isSharedDriveDocument) {
      const url = `${window.location.origin}/editor/${document.id}`;
      copyLink(url, "Document link copied. Access still follows shared drive permissions.");
      return;
    }

    // For public documents, just copy the link
    if (document.visibility === "public") {
      const url = `${window.location.origin}/editor/${document.id}`;
      copyLink(url, "Public link copied to clipboard! Anyone can access this document.");
      return;
    }

    // For private documents, share with specific user
    const email = prompt("Enter user email to share with:");
    if (!email) return;

    const request: ShareDocumentRequest = {
      userEmail: email,
      role: "editor",
    };

    api
      .post<ShareDocumentRequest, ShareDocumentResponse>(
        `/api/documents/${document.id}/permissions`,
        request
      )
      .then(() => alert(`Shared with ${email}`))
      .catch((err) => {
        console.error("Failed to share:", err);
        alert("Failed to share document");
      });
  };

  if (!canRename && !canMove && !canToggleVisibility && !canShare && !canDelete) {
    return null;
  }

  return (
    <Fragment>
      <RowActionsMenu>
        {canRename && (
          <DropdownMenuItem onSelect={handleRename}>
            <Edit className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
        )}

        {canMove && (
          <DropdownMenuItem
            onSelect={() => {
              setMoveOpen(true);
            }}
          >
            <Folder className="h-4 w-4" />
            Move
          </DropdownMenuItem>
        )}

        {canToggleVisibility && (
          <DropdownMenuItem onSelect={handleToggleVisibility}>
            {document.visibility === "private" ? (
              <>
                <Unlock className="h-4 w-4" />
                Make Public
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" />
                Make Private
              </>
            )}
          </DropdownMenuItem>
        )}

        {canShare && (
          <DropdownMenuItem onSelect={handleShare}>
            {isSharedDriveDocument || document.visibility === "public" ? (
              <>
                <Link2 className="h-4 w-4" />
                Copy Link
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                Share with User
              </>
            )}
          </DropdownMenuItem>
        )}

        {canDelete && (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuItem variant="destructive" onSelect={handleDelete}>
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </RowActionsMenu>

      {canMove && (
        <MoveDocumentDialog
          document={document}
          folders={folders}
          open={moveOpen}
          onOpenChange={setMoveOpen}
          onMoved={onUpdate}
        />
      )}
    </Fragment>
  );
}
